import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';

import { useIntl } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';

import { BlockDefault } from '../../BlockBuilder';
import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../../SectionBuilder/SectionContainer';
import SectionTitle from '../SectionTitle';

import { MotionSection, fadeUp, viewportOnce } from '../scrollAnimations';

import css from './OccasionDressingCarousel.module.css';

// First paint state for each card: hidden below its final position so the
// entrance reads as a rise-from-below. Server renders the same state, so
// hydration matches and there is no flash of visible cards.
// Smaller y-offset: cards glide in rather than falling a full viewport height.
const SLIDE_HIDDEN = { opacity: 0, y: 36 };

/**
 * Normalise a label to a slug for fuzzy comparison.
 * E.g. "Black Tie & Gala" → "black-tie-gala"
 */
const slugify = str =>
  (str || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const KNOWN_OCCASIONS = [
  { key: 'wedding-guest', patterns: ['wedding-guest', 'wedding'] },
  { key: 'date-night', patterns: ['date-night', 'date'] },
  { key: 'vacation', patterns: ['vacation', 'holiday', 'resort'] },
  { key: 'black-tie', patterns: ['black-tie', 'black tie', 'gala', 'formal'] },
  { key: 'brunch', patterns: ['brunch', 'cocktail', 'party'] },
  { key: 'office', patterns: ['office', 'work', 'business'] },
];

const DEFAULT_OCCASION_ORDER = [
  'wedding-guest',
  'date-night',
  'vacation',
  'black-tie',
  'brunch',
  'office',
];

const isAutoGeneratedBlockId = id =>
  typeof id === 'string' && (id.includes('-block-') || id.includes('section'));

/**
 * Resolve the occasion filter key (e.g. "black-tie", "wedding-guest")
 * for an occasion carousel card.
 *
 * Checks:
 * 1. Title, text, CTA and image alt content against hosted config options
 * 2. Keyword patterns for known marketplace fashion occasions
 * 3. Fallback to default occasion sequence by card index
 *
 * Never produces invalid auto-generated IDs like "occasion-dressing-section-block-1".
 */
const resolveOccasionKey = (block, occasionOptions = [], index = 0) => {
  const rawTexts = [
    block?.title?.content,
    typeof block?.title === 'string' ? block.title : null,
    block?.text?.content,
    typeof block?.text === 'string' ? block.text : null,
    block?.callToAction?.content,
    typeof block?.callToAction === 'string' ? block.callToAction : null,
    block?.media?.alt,
    !isAutoGeneratedBlockId(block?.blockId) ? block?.blockId : null,
  ].filter(Boolean);

  const cleanTexts = rawTexts.map(t =>
    t
      .replace(/[*_#~`]/g, '')
      .toLowerCase()
      .trim()
  );
  const combinedText = cleanTexts.join(' ');
  const combinedSlug = slugify(combinedText);

  // Match against hosted occasion options from Sharetribe Console
  if (occasionOptions.length > 0) {
    for (const opt of occasionOptions) {
      const optKey = opt.option;
      const optLabelSlug = slugify(opt.label);
      if (
        combinedSlug === optKey ||
        combinedSlug === optLabelSlug ||
        combinedText === opt.label?.toLowerCase() ||
        combinedText === optKey
      ) {
        return optKey;
      }
    }

    for (const opt of occasionOptions) {
      const optKey = opt.option;
      const optLabelSlug = slugify(opt.label);
      if (
        (optKey && combinedSlug.includes(optKey)) ||
        (optLabelSlug && combinedSlug.includes(optLabelSlug))
      ) {
        return optKey;
      }
    }
  }

  // Match against known occasion keywords / patterns
  for (const item of KNOWN_OCCASIONS) {
    for (const pattern of item.patterns) {
      const patSlug = slugify(pattern);
      if (combinedText.includes(pattern) || combinedSlug.includes(patSlug)) {
        return item.key;
      }
    }
  }

  // Fallback to sequential occasion by card index
  if (occasionOptions.length > 0 && occasionOptions[index]) {
    return occasionOptions[index].option;
  }
  return DEFAULT_OCCASION_ORDER[index % DEFAULT_OCCASION_ORDER.length];
};

const SWIPER_BREAKPOINTS = {
  768: {
    slidesPerView: 2.2,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  1024: {
    slidesPerView: 3.2,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  1128: {
    slidesPerView: 4.28,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
};

/**
 * Occasion Dressing carousel.
 * Rendered when Page Builder sectionId is `occasion-dressing-section`.
 *
 * Console: title (`The Art of Occasion **Dressing**`) and blocks
 * with media and category name (title). Optional block CTA makes the card clickable.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Array<Object>?} props.blocks
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const OccasionDressingCarousel = props => {
  const {
    sectionId,
    className,
    rootClassName,
    title,
    description,
    appearance,
    blocks = [],
    options,
  } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  // Swiper is ESM-only. Staging SSR (Node + webpack-node-externals) cannot
  // require() it, so load it on the client after mount.
  const [swiperLib, setSwiperLib] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([import('swiper/react'), import('swiper/modules'), import('swiper/css')])
      .then(([swiperReact, swiperModules]) => {
        if (!cancelled) {
          setSwiperLib({
            Swiper: swiperReact.Swiper,
            SwiperSlide: swiperReact.SwiperSlide,
            A11y: swiperModules.A11y,
          });
        }
      })
      .catch(err => {
        console.warn('Failed to load Swiper', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Retrieve occasion enum options from hosted config if available
  const occasionField = (config?.listing?.listingFields || []).find(f => f.key === 'occasion');
  const occasionOptions = occasionField?.enumOptions || [];

  const hasHeaderFields = hasDataInFields([title, description], fieldOptions);
  const hasBlocks = blocks?.length > 0;
  const swiperRef = useRef(null);
  const [navState, setNavState] = useState({ isBeginning: true, isEnd: true });

  // Use intersection observer to detect when the first card enters the viewport,
  // then stagger-animate all visible slides once. This avoids Swiper's
  // overflow:hidden clipping breaking `whileInView` on individual slides.
  const carouselTrackRef = useRef(null);
  const [carouselRevealed, setCarouselRevealed] = useState(false);
  // Fire as soon as ~12 % of the carousel track is visible so the first cards
  // are already animating by the time they reach the natural reading zone.
  const inView = useInView(carouselTrackRef, { once: true, amount: 0.12 });

  // Stagger-animate the slides exactly once when the carousel scrolls into view.
  // A state flag is used instead of `whileInView` on each slide because Swiper
  // clips and translates slides, which would re-trigger per-slide observers.
  useEffect(() => {
    if (inView && !carouselRevealed) {
      setCarouselRevealed(true);
    }
  }, [inView, carouselRevealed]);

  const updateNav = useCallback(instance => {
    if (!instance) {
      return;
    }
    const next = {
      isBeginning: instance.isBeginning,
      isEnd: instance.isEnd,
    };
    setNavState(prev =>
      prev.isBeginning === next.isBeginning && prev.isEnd === next.isEnd ? prev : next
    );
  }, []);

  const renderBlock = (block, index) => {
    const blockId = block.blockId || `${sectionId}-block-${index + 1}`;

    // Resolve the occasion key matching the listing's publicData.occasion
    // (e.g. "wedding-guest", "date-night", "vacation", "black-tie", "brunch", "office")
    const occasionKey = resolveOccasionKey(block, occasionOptions, index);
    const searchHref = `/s?pub_occasion=has_any:${encodeURIComponent(occasionKey)}`;

    const titleText = block.title?.content || (typeof block.title === 'string' ? block.title : '');
    const ctaLabel =
      block.callToAction?.content ||
      titleText ||
      intl.formatMessage({ id: 'OccasionDressingCarousel.shopNow' });

    const occasionCallToAction = {
      fieldType: 'internalButtonLink',
      href: searchHref,
      content: ctaLabel,
    };

    return (
      <BlockDefault
        {...block}
        blockId={blockId}
        callToAction={occasionCallToAction}
        rootClassName={css.card}
        mediaClassName={css.cardMedia}
        textClassName={css.cardText}
        ctaButtonClass={css.cardCta}
        responsiveImageSizes="(max-width: 767px) 90vw, (max-width: 1023px) 45vw, 25vw"
        options={options}
      />
    );
  };

  const { Swiper, SwiperSlide, A11y } = swiperLib || {};

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(css.root, className)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <MotionSection>
        <div className={css.inner}>
          {hasHeaderFields ? (
            <motion.header
              className={css.header}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              <SectionTitle data={title} className={css.title} />
              <Field data={description} className={css.description} options={fieldOptions} />
            </motion.header>
          ) : null}

          {hasBlocks ? (
            <motion.div
              className={css.carousel}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
            >
              <div ref={carouselTrackRef}>
                {Swiper ? (
                  <Swiper
                    modules={[A11y]}
                    slidesPerView={1.15}
                    slidesPerGroup={1}
                    spaceBetween={16}
                    speed={600}
                    grabCursor
                    watchOverflow
                    observer={false}
                    observeParents={false}
                    breakpoints={SWIPER_BREAKPOINTS}
                    onSwiper={instance => {
                      swiperRef.current = instance;
                      updateNav(instance);
                    }}
                    onSlideChange={updateNav}
                    a11y={{
                      enabled: true,
                      containerMessage: intl.formatMessage({
                        id: 'OccasionDressingCarousel.carousel',
                      }),
                    }}
                  >
                    {blocks.map((block, index) => (
                      <SwiperSlide
                        key={`${block.blockId || `${sectionId}-block-${index + 1}`}_i${index}`}
                      >
                        <motion.div
                          initial={SLIDE_HIDDEN}
                          animate={
                            carouselRevealed
                              ? {
                                  opacity: 1,
                                  y: 0,
                                  transition: {
                                    delay: index * 0.08,
                                    duration: 0.75,
                                    ease: [0.22, 1, 0.36, 1],
                                  },
                                }
                              : { ...SLIDE_HIDDEN, transition: { duration: 0.001 } }
                          }
                          style={{ willChange: 'opacity, transform' }}
                        >
                          {renderBlock(block, index)}
                        </motion.div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <div className={css.card}>{blocks[0] ? renderBlock(blocks[0], 0) : null}</div>
                )}
              </div>
              <div className={css.arrows}>
                {navState.isBeginning ? (
                  <div />
                ) : (
                  <button
                    type="button"
                    className={css.arrowPrev}
                    disabled={navState.isBeginning}
                    onClick={() => swiperRef.current?.slidePrev()}
                    aria-label={intl.formatMessage({ id: 'OccasionDressingCarousel.previous' })}
                  />
                )}

                {navState.isEnd ? (
                  <div />
                ) : (
                  <button
                    type="button"
                    className={css.arrowNext}
                    disabled={navState.isEnd}
                    onClick={() => swiperRef.current?.slideNext()}
                    aria-label={intl.formatMessage({ id: 'OccasionDressingCarousel.next' })}
                  />
                )}
              </div>
            </motion.div>
          ) : null}
        </div>
      </MotionSection>
    </SectionContainer>
  );
};

export default OccasionDressingCarousel;
